import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Send, Users, Briefcase, Wrench, AlertCircle, CheckCircle, Clock, ChevronLeft, Eye } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';

export default function AdminEmailBroadcast() {
  const navigate = useNavigate();
  const [audience, setAudience] = useState('tradespeople');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/email-broadcast/history`, { withCredentials: true });
      setHistory(response.data.broadcasts || []);
    } catch (error) {
      console.warn('Failed to fetch broadcast history', error.message);
    }
  };

  const handleSend = async (isTest = false) => {
    if (!subject.trim()) { toast.error('Subject is required'); return; }
    if (!message.trim()) { toast.error('Message body is required'); return; }
    if (isTest && !testEmail.trim()) { toast.error('Enter a test email address'); return; }

    if (!isTest && !window.confirm(`Send this email to ALL ${audience}? This cannot be undone.`)) return;

    setSending(true);
    try {
      const payload = {
        audience,
        subject: subject.trim(),
        message: message.trim(),
        ...(isTest ? { test_email: testEmail.trim() } : {}),
      };
      const response = await axios.post(`${BACKEND_URL}/api/admin/email-broadcast/send`, payload, { withCredentials: true });
      const data = response.data;

      if (data.success) {
        toast.success(data.message);
        if (!isTest) {
          setSubject('');
          setMessage('');
          fetchHistory();
        }
      } else {
        toast.error(data.message || 'Broadcast failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send broadcast');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <button onClick={() => navigate('/admin/dashboard')} className="flex items-center gap-1 text-slate-600 hover:text-slate-900 mb-6 text-sm" data-testid="back-to-admin">
          <ChevronLeft className="h-4 w-4" /> Back to Admin
        </button>

        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
            <Mail className="h-5 w-5 text-orange-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Email Broadcast</h1>
            <p className="text-sm text-slate-500">Send emails to all tradespeople or all employers</p>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
          {/* Audience Selector */}
          <label className="block text-sm font-medium text-slate-700 mb-2">Audience</label>
          <div className="grid grid-cols-2 gap-3 mb-6">
            <button
              onClick={() => setAudience('tradespeople')}
              className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${audience === 'tradespeople' ? 'border-orange-500 bg-orange-50' : 'border-slate-200 hover:border-slate-300'}`}
              data-testid="audience-tradespeople"
            >
              <Wrench className={`h-5 w-5 ${audience === 'tradespeople' ? 'text-orange-600' : 'text-slate-400'}`} />
              <div className="text-left">
                <p className={`font-semibold ${audience === 'tradespeople' ? 'text-orange-700' : 'text-slate-700'}`}>All Tradespeople</p>
                <p className="text-xs text-slate-500">Send to every registered tradesperson</p>
              </div>
            </button>
            <button
              onClick={() => setAudience('employers')}
              className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${audience === 'employers' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}
              data-testid="audience-employers"
            >
              <Briefcase className={`h-5 w-5 ${audience === 'employers' ? 'text-blue-600' : 'text-slate-400'}`} />
              <div className="text-left">
                <p className={`font-semibold ${audience === 'employers' ? 'text-blue-700' : 'text-slate-700'}`}>All Employers</p>
                <p className="text-xs text-slate-500">Send to every registered employer</p>
              </div>
            </button>
          </div>

          {/* Subject */}
          <label className="block text-sm font-medium text-slate-700 mb-1">Subject</label>
          <input
            type="text"
            value={subject}
            onChange={e => setSubject(e.target.value)}
            placeholder="Email subject line..."
            className="w-full mb-4"
            data-testid="broadcast-subject"
          />

          {/* Message */}
          <label className="block text-sm font-medium text-slate-700 mb-1">Message</label>
          <textarea
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder="Write your email message here..."
            rows={8}
            className="w-full mb-4"
            data-testid="broadcast-message"
          />

          {/* Preview */}
          {showPreview && subject && message && (
            <div className="mb-4 border border-slate-200 rounded-lg overflow-hidden">
              <div className="bg-slate-100 px-4 py-2 text-xs font-medium text-slate-600">Preview</div>
              <div className="p-4">
                <div className="bg-gradient-to-r from-orange-500 to-amber-500 p-4 rounded-t-lg text-center">
                  <h2 className="text-white font-bold text-lg">OnsiteHand</h2>
                </div>
                <div className="border border-t-0 border-slate-200 rounded-b-lg p-4">
                  <h3 className="font-semibold text-slate-900 mb-2">{subject}</h3>
                  <p className="text-slate-600 whitespace-pre-wrap">{message}</p>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="flex items-center justify-center gap-2 px-4 py-2.5 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 transition-colors"
            >
              <Eye className="h-4 w-4" /> {showPreview ? 'Hide Preview' : 'Preview'}
            </button>

            <div className="flex items-center gap-2 flex-1">
              <input
                type="email"
                value={testEmail}
                onChange={e => setTestEmail(e.target.value)}
                placeholder="test@email.com"
                className="flex-1"
                data-testid="broadcast-test-email"
              />
              <button
                onClick={() => handleSend(true)}
                disabled={sending}
                className="flex items-center gap-2 px-4 py-2.5 border border-blue-200 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50 whitespace-nowrap"
                data-testid="broadcast-test-btn"
              >
                <Mail className="h-4 w-4" /> Test Send
              </button>
            </div>

            <button
              onClick={() => handleSend(false)}
              disabled={sending || !subject.trim() || !message.trim()}
              className="flex items-center justify-center gap-2 px-6 py-2.5 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg font-semibold hover:from-orange-600 hover:to-amber-600 transition-all disabled:opacity-50"
              data-testid="broadcast-send-btn"
            >
              {sending ? <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" /> : <Send className="h-4 w-4" />}
              {sending ? 'Sending...' : `Send to All ${audience === 'tradespeople' ? 'Tradespeople' : 'Employers'}`}
            </button>
          </div>
        </div>

        {/* Broadcast History */}
        {history.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Clock className="h-5 w-5 text-slate-400" /> Broadcast History
            </h2>
            <div className="space-y-3">
              {history.map((b) => (
                <div key={b.broadcast_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg text-sm">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-900 truncate">{b.subject}</p>
                    <p className="text-xs text-slate-500">
                      To: {b.audience} &middot; {new Date(b.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                    {b.status === 'completed' ? (
                      <span className="flex items-center gap-1 text-xs text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">
                        <CheckCircle className="h-3 w-3" /> {b.sent}/{b.total_recipients}
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-full">
                        <AlertCircle className="h-3 w-3" /> {b.status}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
